import { collection, limit, onSnapshot, query, where, type Firestore } from 'firebase/firestore';
import { createClockOffsetTracker, getDeviceId, type ClockOffsetTracker } from './device';
import { createConnectivity, type Connectivity } from './connectivity';
import { createCommandWriter, type CommandWriter } from './commands';
import { createEchoView, type EchoView } from './echo';
import { initEcoBox, type EcoBoxClient, type InitOptions } from './init';
import { leaseNumbers } from './counter';
import { createLeaseManager, mayLease, type LeaseManager } from './leases';
import { createLedger, type Ledger } from './ledger';
import { getPref, setPref } from './prefs';
import {
  createStaffSession,
  permissionsFromClaims,
  scopeBranches,
  type StaffSession,
} from './auth';
import { createStore, type ReadableStore } from './store';
import { createSyncStatus, type SyncStatus } from './sync';
import { createUploadQueue, type UploadQueue } from './uploads';

/** Used only until the `branches` collection has loaded, for people whose scope is "all". */
export const PHASE0_BRANCHES: readonly string[] = [];

export interface BranchState {
  /** Branch codes the person may work at. */
  options: readonly string[];
  /** The branch commands are sent for, or null until the claims are known. */
  current: string | null;
}

export interface BranchStore extends ReadableStore<BranchState> {
  select(branch: string): void;
}

export interface EcoBoxServices {
  client: EcoBoxClient;
  deviceId: string;
  clock: ClockOffsetTracker;
  connectivity: Connectivity;
  ledger: Ledger;
  writer: CommandWriter;
  sync: SyncStatus;
  session: StaffSession;
  branch: BranchStore;
  echoes: EchoView;
  /** Tracking and receipt numbers leased to this device (work offline). */
  leases: LeaseManager;
  /** Photos and signatures waiting to upload, kept in IndexedDB (02 §2.2). */
  uploads: UploadQueue;
  /** Start every background piece, once. Returns a function that stops them (tests only: the app runs for its lifetime). */
  start(): Promise<() => void>;
}

export interface BootstrapOptions extends InitOptions {
  appVersion: string;
}

function createBranchStore(session: StaffSession, db?: Firestore): BranchStore {
  const store = createStore<BranchState>({ options: [], current: null });
  let configured: readonly string[] = PHASE0_BRANCHES;
  // Branches in an origin country come first as the default: that is where the counter works.
  let originBranches = new Set<string>();
  let branchSectors = new Map<string, string>();
  let originSectors = new Set<string>();
  // Only a branch the person picked sticks; an automatic default is re-evaluated as data loads.
  let chosen = false;
  const refreshOrigin = (): void => {
    originBranches = new Set(
      [...branchSectors].filter(([, sector]) => originSectors.has(sector)).map(([code]) => code),
    );
  };
  const compute = (): void => {
    const { claims } = session.getSnapshot();
    if (!claims) return;
    const options = scopeBranches(claims.scope) ?? configured;
    const saved = getPref('lastBranch');
    const prev = store.getSnapshot().current;
    const current =
      (prev && chosen && options.includes(prev) ? prev : null) ??
      (saved && options.includes(saved) ? saved : null) ??
      options.find((b) => originBranches.has(b)) ??
      options[0] ??
      null;
    store.set({ options, current });
  };
  session.subscribe(compute);
  if (db) {
    // Everyone with scope "all" picks from the configured, active branches (Settings → Countries & branches).
    let stop: (() => void) | undefined;
    session.subscribe(() => {
      const { isStaff } = session.getSnapshot();
      if (isStaff && !stop) {
        const stopBranches = onSnapshot(
          query(collection(db, 'branches'), where('active', '==', true), limit(200)),
          (snap) => {
            configured = snap.docs.map((d) => d.id).sort();
            branchSectors = new Map(snap.docs.map((d) => [d.id, String(d.get('sectorId'))]));
            refreshOrigin();
            compute();
          },
          (error) => console.warn('Could not load branches', error.message),
        );
        const stopSectors = onSnapshot(
          query(collection(db, 'sectors'), where('type', '==', 'origin'), limit(50)),
          (snap) => {
            originSectors = new Set(snap.docs.map((d) => d.id));
            refreshOrigin();
            compute();
          },
          (error) => console.warn('Could not load countries', error.message),
        );
        stop = () => {
          stopBranches();
          stopSectors();
        };
      } else if (!isStaff && stop) {
        stop();
        stop = undefined;
      }
    });
  }
  return {
    subscribe: store.subscribe,
    getSnapshot: store.getSnapshot,
    select(branch) {
      const { options } = store.getSnapshot();
      if (!options.includes(branch)) return;
      chosen = true;
      setPref('lastBranch', branch);
      store.set({ options, current: branch });
    },
  };
}

export async function bootstrapEcoBox(options: BootstrapOptions): Promise<EcoBoxServices> {
  const client = initEcoBox(options);
  const deviceId = await getDeviceId();
  const clock = createClockOffsetTracker();
  await clock.load();
  const connectivity = createConnectivity();
  const ledger = createLedger();
  const session = createStaffSession({ auth: client.auth, db: client.db });
  const branch = createBranchStore(session, client.db);
  const writer = createCommandWriter({
    db: client.db,
    auth: client.auth,
    deviceId,
    appVersion: options.appVersion,
    getBranchId: () => branch.getSnapshot().current,
    clockOffset: () => clock.get(),
    ledger,
    connectivity,
  });
  const uploads = createUploadQueue({
    getUid: () => client.auth.currentUser?.uid ?? null,
    connectivity,
    storage: client.storage,
  });
  const sync = createSyncStatus({ db: client.db, ledger, connectivity, uploads });
  const echoes = createEchoView({ db: client.db, writer, session });
  const leases = createLeaseManager({
    deviceId,
    connectivity,
    request: (input) => leaseNumbers(client.functions, input),
    allowed: (series) => {
      const permissions = permissionsFromClaims(session.getSnapshot().claims);
      return mayLease(series, (p) => permissions.has(p));
    },
  });

  let started: Promise<() => void> | undefined;
  const run = async (): Promise<() => void> => {
    session.start();
    sync.start();
    echoes.start();
    await writer.start();
    const stopLeases = leases.watch(branch);
    const stopUploads = uploads.start();
    const measure = (): void => {
      if (connectivity.getSnapshot().online) void clock.measureViaHttp('/');
    };
    const stopMeasuring = connectivity.subscribe(measure);
    measure();
    return () => {
      stopMeasuring();
      stopLeases();
      stopUploads();
      writer.dispose();
      echoes.dispose();
      sync.dispose();
      session.dispose();
    };
  };

  return {
    client,
    deviceId,
    clock,
    connectivity,
    ledger,
    writer,
    sync,
    session,
    branch,
    echoes,
    leases,
    uploads,
    start() {
      started ??= run();
      return started;
    },
  };
}
